import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AttachmentService } from '@common/services/attachment.service';
import { CodelistService } from '@common/services/codelist.service';
import { HelpModuleService } from '@common/services/help-module.service';
import { ModalService } from '@common/services/modal.service';
import { NavigationFactoryService } from '@common/services/navigation-factory.service';
import { marker } from '@common/translate-marker';
import { YouTubeUtils } from '@common/utils/youtube.utils';
import { requiredValidator } from '@common/validators/required-validator';
import { youtubeUrlValidator } from '@common/validators/youtube-url-validator';
import { TranslateService } from '@ngx-translate/core';
import { DialogCloseResult, DialogRef } from '@progress/kendo-angular-dialog';
import { FileRestrictions } from '@progress/kendo-angular-upload';
import { ToastService } from '@services/toast.service';
import { filter } from 'rxjs';

@Component({
    selector: 'app-add-document-modal',
    templateUrl: './add-document-modal.component.html',
    styleUrls: ['./add-document-modal.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddDocumentModalComponent implements OnInit {
    isBusy: boolean;
    form: UntypedFormGroup;
    isEditMode: boolean;
    document: any;
    categories: any[] = [];
    subCategories: any[] = [];
    newAttachments: any[] = [];

    public attachmentFileUploadRestrictions: FileRestrictions = {
        allowedExtensions: new Array<string>(),
        maxFileSize: 0
    };
    files: Array<any> = [];
    attachmentsToRemove: number[] = [];
    youtubePreviewUrl: SafeResourceUrl | null = null;

    constructor(
        public dialogService: ModalService,
        public activeModal: DialogRef,
        private translateService: TranslateService,
        private toastyService: ToastService,
        public service: HelpModuleService,
        public codelistService: CodelistService,
        private navigationFactoryService: NavigationFactoryService,
        private attachmentService: AttachmentService,
        private sanitizer: DomSanitizer
    ) {}

    ngOnInit() {
        this.form = this.createFormGroup();
        this.loadAttachmentSettings();
        this.getData();
        this.checkEditMode();
        this.setupYouTubePreview();
    }

    public addDocument() {
        const formValue = this.form.getRawValue();
        // helpAttachments only drives the file list in the UI and is ignored by the server,
        // but re-sending the already stored attachments pushes the request over the max body size
        delete formValue.helpAttachments;
        const body = { ...formValue, files: this.files };
        if (this.attachmentsToRemove.length > 0) {
            body['removedAttachmentIds'] = this.attachmentsToRemove;
        }
        this.service.addDocument(body).subscribe({
            next: () => {
                this.toastyService.success(
                    this.translateService.instant(
                        this.isEditMode
                            ? marker('Document updated successfully')
                            : marker('Document added successfully')
                    )
                );
                this.activeModal.close();
            }
        });
    }

    fileSelected($event) {
        if ($event.files.length > 10) {
            this.toastyService.error(this.translateService.instant(marker('Please select maximum of 10 files')));
            $event.preventDefault();
            return;
        }

        $event.files.forEach((element) => {
            if (element.size > this.attachmentFileUploadRestrictions.maxFileSize) {
                this.toastyService.error(
                    `${this.translateService.instant(marker('Attachment size invalid'))} - ${element.name}`
                );
                return;
            }
            if (!this.attachmentFileUploadRestrictions.allowedExtensions.some((ext) => ext == element.extension)) {
                this.toastyService.error(
                    `${this.translateService.instant(marker('Invalid attachment'))} - ${element.name}`
                );
                return;
            }

            const raw = element.rawFile;
            if (raw) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.documentOnLoad(e.target.result.toString(), raw.name);
                };
                reader.readAsDataURL(element.rawFile);
                this.newAttachments.push(element);
            }
        });
    }

    removeAttachment(uid: string, attachmentId?: number) {
        const newAttachment = this.newAttachments.find((x) => x.uid == uid);
        if (newAttachment) {
            const file = this.files.filter((x) => x.base64ContentName == newAttachment.name);
            if (file == null || file.length == 0) return;
            this.files.splice(this.files.indexOf(file[0]), 1);
            this.form
                .get('helpAttachments')
                .patchValue(this.form.get('helpAttachments').value.filter((x) => x.name != newAttachment.name));
            return;
        }
        this.dialogService
            .confirm(
                this.translateService.instant(marker('Remove Attachment')),
                this.translateService.instant(
                    marker(
                        'Are you sure you want to remove the attachment? The changes will be applied when you save the user manual.'
                    )
                ),
                this.translateService.instant(marker('Remove')),
                this.translateService.instant(marker('Cancel'))
            )
            .pipe(filter((x) => !(x instanceof DialogCloseResult)))
            .subscribe((res) => {
                if (res) {
                    this.form
                        .get('helpAttachments')
                        .patchValue(this.form.get('helpAttachments').value.filter((x) => x.uid != uid));
                    this.attachmentsToRemove.push(attachmentId);
                }
            });
    }

    documentOnLoad(file: string, name: string) {
        this.files.push({ base64Content: file, base64ContentName: name });
    }

    bytesToKB(bytes: number): string {
        const kilobytes = bytes / 1024;
        return kilobytes.toFixed(2) + ' KB';
    }

    getYouTubeEmbedUrl(): SafeResourceUrl | null {
        return this.youtubePreviewUrl;
    }

    private setupYouTubePreview() {
        this.form.get('youtubeURL')?.valueChanges.subscribe((url) => {
            if (url && YouTubeUtils.isValidYouTubeUrl(url)) {
                const embedUrl = YouTubeUtils.getEmbedUrl(url);
                this.youtubePreviewUrl = embedUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl) : null;
            } else {
                this.youtubePreviewUrl = null;
            }
        });

        // Set initial preview if editing
        const initialUrl = this.form.get('youtubeURL')?.value;
        if (initialUrl && YouTubeUtils.isValidYouTubeUrl(initialUrl)) {
            const embedUrl = YouTubeUtils.getEmbedUrl(initialUrl);
            this.youtubePreviewUrl = embedUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl) : null;
        }
    }

    private loadAttachmentSettings(): void {
        this.attachmentService.getAttachmentSettingsForUpload().subscribe((response) => {
            this.attachmentFileUploadRestrictions = response;
        });
    }

    private checkEditMode() {
        if (this.document) {
            this.isEditMode = true;
            this.form.patchValue(this.document);
            if (this.document.helpAttachmentsCount > 0) {
                this.isBusy = true;
                this.service.getDocumentAttachments(this.document.id).subscribe((response) => {
                    // keep only what the file list renders - the base64 content would otherwise
                    // stay in the form value and be sent back on every save
                    const attachments = response.map((attachment) => ({
                        id: attachment.id,
                        name: attachment.attachedFile.name,
                        size: Math.round(attachment.attachedFile.base64Content.length * (3 / 4) - 2)
                    }));
                    this.form.get('helpAttachments').patchValue(attachments);
                    this.isBusy = false;
                });
            }
        }
    }

    private createFormGroup() {
        const form = new UntypedFormGroup({
            id: new UntypedFormControl(0),
            parentCategoryId: new UntypedFormControl(null, requiredValidator()),
            helpCategoryId: new UntypedFormControl({ value: 0, disabled: true }),
            name: new UntypedFormControl(null, requiredValidator()),
            nameEN: new UntypedFormControl(null),
            description: new UntypedFormControl(null, requiredValidator()),
            descriptionEN: new UntypedFormControl(null),
            youtubeURL: new UntypedFormControl(null, youtubeUrlValidator()),
            helpAttachments: new UntypedFormControl(null)
        });
        form.patchValue(this);
        return form;
    }

    private getData() {
        this.categories = this.navigationFactoryService
            .getMenuItems()
            .map((category) => ({ name: category.title, id: category.id }))
            .filter((category) => category.name !== 'Help');
        this.trackFromChanges();
    }

    private trackFromChanges() {
        this.form
            .get('parentCategoryId')
            .valueChanges.pipe(filter((x) => x !== null))
            .subscribe({
                next: (categoryId: number) => {
                    if (categoryId) {
                        this.form.get('helpCategoryId').enable();
                        this.subCategories = this.navigationFactoryService
                            .getMenuItems()
                            .find((x) => x.id === categoryId)
                            .childs.map((category) => ({ name: category.title, id: category.id }));
                    } else {
                        this.form.get('helpCategoryId').disable();
                    }
                }
            });
    }
}
